import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import type { ApiError } from '@/core/api';
import { resolveEmployeeLabel, searchEmployees } from '@/features/hrm/employees/select';
import { PDP_STATUSES } from '../constants';
import type { GoalStatus, PDPInput } from '../api';

const GOAL_STATUS_OPTIONS: { value: GoalStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
];

const schema = z.object({
  employee: z.string().min(1, 'Employee is required'),
  title: z.string().trim().min(1, 'Title is required').max(255),
  period: z.string().max(50),
  status: z.enum(['active', 'completed']),
  summary: z.string(),
  goals: z.array(z.object({ title: z.string(), status: z.enum(['open', 'in_progress', 'done']) })),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = ['employee', 'title', 'period', 'status', 'summary'] as const;

interface PdpFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  /** Show the seed-goals editor (create only — goals are edited on the detail page). */
  showGoals?: boolean;
  onSubmit: (values: PDPInput) => Promise<unknown>;
  onCancel: () => void;
}

export function PdpForm({
  defaultValues,
  submitLabel,
  showGoals = false,
  onSubmit,
  onCancel,
}: PdpFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      employee: '',
      title: '',
      period: '',
      status: 'active',
      summary: '',
      goals: [],
      ...defaultValues,
    },
  });

  const goals = useFieldArray({ control: form.control, name: 'goals' });

  async function handleSubmit(values: FormValues) {
    const input: PDPInput = {
      employee: Number(values.employee),
      title: values.title,
      period: values.period,
      status: values.status,
      summary: values.summary,
      goals: showGoals
        ? values.goals
            .map((g) => ({ title: g.title.trim(), status: g.status }))
            .filter((g) => g.title)
        : undefined,
    };
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
        <FormSection variant="card" title="Plan" description="Who this plan is for and what it covers.">
          <FormField
            control={form.control}
            name="employee"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Employee</FormLabel>
                <FormControl>
                  <EntitySelect
                    queryKey="pdp-employee-picker"
                    value={field.value}
                    onChange={field.onChange}
                    search={searchEmployees}
                    resolveLabel={resolveEmployeeLabel}
                    placeholder="Select an employee"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Grow into Senior Engineer" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection variant="card" title="Schedule" description="Review period and current status.">
          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="period"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Period</FormLabel>
                  <FormControl>
                    <Input placeholder="2026" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PDP_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection variant="card" title="Summary" description="Narrative context for this plan.">
          <FormField
            control={form.control}
            name="summary"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Summary</FormLabel>
                <FormControl>
                  <MarkdownEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Focus areas, milestones, development goals…"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {showGoals ? (
          <FormSection
            variant="card"
            title="Goals"
            description="What this person is working toward — add them up front or later."
          >
            <div className="space-y-2">
              {goals.fields.map((row, index) => (
                <div key={row.id} className="flex items-center gap-2">
                  <Input
                    {...form.register(`goals.${index}.title`)}
                    placeholder="e.g. Lead 3 design reviews"
                    className="flex-1"
                  />
                  <FormField
                    control={form.control}
                    name={`goals.${index}.status`}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="w-36" aria-label="Goal status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {GOAL_STATUS_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => goals.remove(index)}
                    aria-label="Remove goal"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => goals.append({ title: '', status: 'open' })}
              >
                <Plus className="size-4" /> Add goal
              </Button>
            </div>
          </FormSection>
        ) : null}

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
